const MODULE_NAME = "_54_trade_table_options";

import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";

const DATE_FILTERS = [
    { label: "All", value: "all" },
    { label: "Past 90 Days", value: "90days" },
    { label: "Year-to-Date", value: "ytd" }
];

const POSITION_STATUS_OPTIONS = ["All", "Open", "Closed", "Expired", "Assigned"];

function Tooltip({ text }) {
    return (
        <span
            style={{
                marginLeft: 4,
                verticalAlign: "middle",
                cursor: "pointer",
                color: "#888"
            }}
            title={text}
        >
            🛈
        </span>
    );
}

// Simple transpose icon (↔️) - REMOVED
// Inline style for row highlight (applies to both table layouts) - REMOVED

function OptionsTradeTable({ activeDate, latest_date, refreshKey }) {
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    let dateFromQuery = queryParams.get("date");
    if (!dateFromQuery || !/^\d{4}-\d{2}-\d{2}$/.test(dateFromQuery)) {
        dateFromQuery = null;
    }
    const effectiveDate = dateFromQuery
        || (latest_date && /^\d{4}-\d{2}-\d{2}$/.test(latest_date) ? latest_date : null)
        || (sessionStorage.getItem("latest_date") && /^\d{4}-\d{2}-\d{2}$/.test(sessionStorage.getItem("latest_date")) ? sessionStorage.getItem("latest_date") : null)
        || new Date().toISOString().slice(0, 10);

    const [data, setData] = useState(null);
    const [error, setError] = useState("");
    // const [transposed, setTransposed] = useState(false); // Removed
    // const [hoveredRow, setHoveredRow] = useState(null); // Removed
    const [collapsed, setCollapsed] = useState(true);
    const [statusUpdating, setStatusUpdating] = useState({});

    // New filter states
    const [dateFilter, setDateFilter] = useState("all");
    const [positionStatusFilter, setPositionStatusFilter] = useState("All");

    useEffect(() => {
        setError("");
        setData(null);
        if (!effectiveDate) {
            setError("No date available for options trade data.");
            return;
        }
        fetch(
            `${process.env.REACT_APP_API_URL}/api/options-trade-table?date=${effectiveDate}&component=_54_today_trade_options`,
            { credentials: "include" }
        )
            .then(res => res.json())
            .then(data => setData(data))
            .catch(() => setError("Could not fetch options trade data"));
    }, [effectiveDate, refreshKey, location.search]);

    if (error) return <div>{error}</div>;
    if (!data) return <div>Loading...</div>;
    if (data.error && data.error.toLowerCase().includes("no options trades")) {
        return null;
    }
    if (data.error) return <div>{data.error}</div>;

    // Filtering logic (date filter now uses Trade_Date)
    function filterRows(rows) {
        let filtered = [...rows];
        if (dateFilter !== "all") {
            const now = new Date();
            filtered = filtered.filter(row => {
                if (!row.Trade_Date) return false;
                // Expecting MM/DD/YY
                const [mm, dd, yy] = row.Trade_Date.split("/");
                const rowDate = new Date(`20${yy}`, mm - 1, dd);
                if (dateFilter === "90days") {
                    const diff = (now - rowDate) / (1000 * 60 * 60 * 24);
                    return diff <= 90;
                }
                if (dateFilter === "ytd") {
                    return rowDate.getFullYear() === now.getFullYear();
                }
                return true;
            });
        }
        if (positionStatusFilter !== "All") {
            filtered = filtered.filter(row =>
                row.position_status === positionStatusFilter
            );
        }
        return filtered;
    }

    // Use raw backend data, no grouping or Qty_buy filter
    const sortedAndGroupedOptions = Array.isArray(data.summary)
        ? sortAndGroupOptionsRows(filterRows(data.summary))
        : [];
                
    if (sortedAndGroupedOptions.length === 0) {
        return null;
    }

    // Columns to display, matching backend fields
    const friendlyColumns = {
        Symbol: "Symbol",
        Type: "Type", // Use the "Type" field from the backend response
        Pos_Effect: "Pos Effect",
        Side: "Side",
        Qty: "Qty",
        Price: "Price",
        Strike: "Strike",
        Exp: "Expiration",
        Trade_Date: "Trade Date",
        position_status: "Position Status"
        // Add more fields if needed
    };

    const optionsTooltip = data.tooltip;

    // Handler to update position_status
    async function handleStatusChange(row, newStatus, rowIndex) {
        setStatusUpdating(prev => ({ ...prev, [rowIndex]: true }));
        try {
            await axios.post(`${process.env.REACT_APP_API_URL}/api/update-position-status`, {
                // Do not send user_id to backend, rely on flask_login session/cookie
                symbol: row.Symbol,
                exp: row.Exp,
                strike: row.Strike,
                option_type: row.option_type,
                position_status: newStatus
            }, { withCredentials: true } // Added this option

            );
            
            // Update data by matching record properties, not array index
            setData(prev => {
                if (!prev || !Array.isArray(prev.summary)) return prev;
                const updatedSummary = prev.summary.map(r => {
                    // Match by Symbol, Exp, Strike, and Type instead of array index
                    if (r.Symbol === row.Symbol && 
                        r.Exp === row.Exp && 
                        r.Strike === row.Strike && 
                        r.Type === row.Type) {
                        return { ...r, position_status: newStatus };
                    }
                    return r;
                });
                return { ...prev, summary: updatedSummary };
            });
        } catch (err) {
            alert("Failed to update position status.");
        } finally {
            setStatusUpdating(prev => ({ ...prev, [rowIndex]: false }));
        }
    }

    function formatHeadlineDate(dateStr) {
        if (!dateStr) return "";
        const isoMatch = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})/);
        if (isoMatch) {
            const [year, month, day] = [isoMatch[1], isoMatch[2], isoMatch[3]];
            const dateObj = new Date(`${year}-${month}-${day}T00:00:00`);
            if (!isNaN(dateObj)) {
                const weekday = dateObj.toLocaleDateString("en-US", { weekday: "short" });
                const monthAbbr = dateObj.toLocaleDateString("en-US", { month: "short" });
                const dayNum = dateObj.getDate();
                return `${weekday} ${monthAbbr} ${dayNum}`;
            }
        }
        const dateObj = new Date(dateStr);
        if (!isNaN(dateObj)) {
            const weekday = dateObj.toLocaleDateString("en-US", { weekday: "short" });
            const monthAbbr = dateObj.toLocaleDateString("en-US", { month: "short" });
            const dayNum = dateObj.getDate();
            return `${weekday} ${monthAbbr} ${dayNum}`;
        }
        return dateStr;
    }

    // Sorting and grouping function
    function sortAndGroupOptionsRows(rows) {
        // Parse MM/DD/YY to Date object
        const parseDate = (d) => {
            if (!d) return new Date(0);
            const [mm, dd, yy] = d.split("/");
            return new Date(`20${yy}`, mm - 1, dd);
        };

        // Group by Symbol, Type, Strike, Exp (exclude Side since TO OPEN/TO CLOSE can have different sides)
                const groupKey = row =>
                    [row.Symbol, row.Type, row.Strike, row.Exp].join("|");

                // Group rows
                const groups = {};
                rows.forEach(row => {
                    const key = groupKey(row);
                    if (!groups[key]) groups[key] = [];
                    groups[key].push(row);
                });

                // Sort each group: TO OPEN first, then TO CLOSE, then by Trade_Date
                Object.values(groups).forEach(groupRows => {
                    groupRows.sort((a, b) => {
                        const effectA = (a.Pos_Effect || "").toUpperCase();
                        const effectB = (b.Pos_Effect || "").toUpperCase();
                        if (effectA === "TO OPEN" && effectB === "TO CLOSE") return -1;
                        if (effectA === "TO CLOSE" && effectB === "TO OPEN") return 1;
                        // If both are the same, sort by Trade_Date
                        const dateA = parseDate(a.Trade_Date);
                        const dateB = parseDate(b.Trade_Date);
                        return dateA - dateB;
                    });
                });

                // Flatten groups, sorted by first Trade_Date in each group, then by Symbol, Type, Strike, Exp
                const sortedKeys = Object.keys(groups).sort((a, b) => {
                    const aRow = groups[a][0], bRow = groups[b][0];
                    const dateA = parseDate(aRow.Trade_Date);
                    const dateB = parseDate(bRow.Trade_Date);
                    if (dateA < dateB) return -1;
                    if (dateA > dateB) return 1;
                    const keys = ["Symbol", "Type", "Strike", "Exp"];
                    for (let key of keys) {
                        if ((aRow[key] || "") < (bRow[key] || "")) return -1;
                        if ((aRow[key] || "") > (bRow[key] || "")) return 1;
                    }
                    return 0;
                });

        // For card view, we return the sorted list of groups (array of arrays), not flattened rows
        // For table view, we might still want flattened rows, but user requested card view replacement.
        // We'll return an array of group objects { key, rows: [...] }
        return sortedKeys.map(key => ({ key, rows: groups[key] }));
    }

    function testOptionPL() {
        fetch("/api/test-option-pl", {
          method: "GET",
          credentials: "include"
        })
          .then((res) => res.json())
          .then((data) => {
            alert(
              data.output
                ? "Option P/L test results printed to terminal."
                : "Error: " + (data.error || "Unknown error")
            );
          })
          .catch((err) => {
            alert("Error: " + err.message);
          });
      }

    // Example: optionsData is your array of option positions
    const closedOptions = data.summary.filter(option => option.position_status !== 'Open');
    const totalPL = typeof data.total_options_pl === "number" ? data.total_options_pl : 0;

    return (
        <div
            className={`collapse-toggle-container${collapsed ? " collapsed" : ""}`}
            data-analytics={MODULE_NAME}
        >
                    <h4
                        className="collapse-toggle-headline"
                        onClick={() => setCollapsed(!collapsed)}
                        style={{ color: "#fff", cursor: "pointer", display: "flex", alignItems: "center" }}
                    >
                        📝 Options – {formatHeadlineDate(data.date)}
                        <Tooltip text={optionsTooltip} />
                    </h4>
            <div style={{ margin: "8px 0", display: "flex", gap: 16, color: "#fff" }}>
                <label>
                    Date Filter:&nbsp;
                    <select
                        value={dateFilter}
                        onChange={e => setDateFilter(e.target.value)}
                        style={{ minWidth: 120, padding: "2px 6px", background: "#1a1a1a", color: "#fff", border: "1px solid #444", borderRadius: 4 }}
                    >
                        {DATE_FILTERS.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                    </select>
                </label>
                <label>
                    Position Status:&nbsp;
                    <select
                        value={positionStatusFilter}
                        onChange={e => setPositionStatusFilter(e.target.value)}
                        style={{ minWidth: 120, padding: "2px 6px", background: "#1a1a1a", color: "#fff", border: "1px solid #444", borderRadius: 4 }}
                    >
                        {POSITION_STATUS_OPTIONS.map(opt => (
                            <option key={opt} value={opt}>{opt}</option>
                        ))}
                    </select>
                </label>
            </div>
                    <div className="collapse-content">
                        <OptionCards 
                            groupedData={sortedAndGroupedOptions} 
                            handleStatusChange={handleStatusChange}
                            statusUpdating={statusUpdating}
                            friendlyColumns={friendlyColumns}
                        />
                    </div>
                    {/* Total P/L after the last card */}
                    <div style={{ marginTop: '24px', textAlign: 'center', fontWeight: 'bold', fontSize: '1.2em', color: '#fff' }}>
                      Total Profit/Loss (Closed Options): ${totalPL.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </div>
                </div>
            );
        }

function OptionCards({ groupedData, handleStatusChange, statusUpdating }) {
    const [collapsedSections, setCollapsedSections] = useState({
        Closed: true,
        Expired: true,
        Assigned: true
    });

    if (!groupedData || groupedData.length === 0) return <div>No options trades found.</div>;

    // Group cards by their current status
    const statuses = ["Open", "Closed", "Expired", "Assigned"];
    const groupsByStatus = {
        Open: [],
        Closed: [],
        Expired: [],
        Assigned: []
    };

    groupedData.forEach(group => {
        const status = group.rows[0].position_status || "Open";
        if (groupsByStatus[status]) {
            groupsByStatus[status].push(group);
        } else {
            // Fallback for unexpected statuses
            if (!groupsByStatus["Open"]) groupsByStatus["Open"] = [];
            groupsByStatus["Open"].push(group);
        }
    });

    const toggleSection = (s) => {
        setCollapsedSections(prev => ({ ...prev, [s]: !prev[s] }));
    };

    const getBadgeColor = (status) => {
        const s = (status || "").toLowerCase();
        if (s === "open") return "#2196F3";
        if (s === "closed") return "#4CAF50";
        if (s === "expired") return "#9E9E9E";
        if (s === "assigned") return "#FF9800";
        return "#888";
    };

    return (
        <div style={{ display: "flex", flexDirection: "column", gap: 16, marginTop: 16 }}>
            {statuses.map(status => {
                const statusGroups = groupsByStatus[status];
                if (statusGroups.length === 0) return null;

                const isCollapsed = !!collapsedSections[status];

                return (
                    <div key={status} style={{ marginBottom: 8 }}>
                        <div 
                            className="status-section-header" 
                            onClick={() => toggleSection(status)}
                            style={{
                                cursor: "pointer",
                                display: "flex",
                                alignItems: "center",
                                gap: 8,
                                padding: "8px 12px",
                                background: "#1a1a1a", // Changed from #f8f9fa
                                borderRadius: 6,
                                marginBottom: 12,
                                borderLeft: `4px solid ${getBadgeColor(status)}`,
                                color: "#eee" // Ensure text is visible
                            }}
                        >
                            <span style={{ fontSize: 12 }}>{isCollapsed ? "▶" : "▼"}</span>
                            <span style={{ fontWeight: "bold", fontSize: 15, textTransform: "uppercase" }}>
                                {status} ({statusGroups.length})
                            </span>
                        </div>

                        {!isCollapsed && (
                            <div style={{ display: "flex", flexDirection: "column", gap: 16, paddingLeft: 4 }}>
                                {statusGroups.map((groupObj) => {
                                    const { rows } = groupObj;
                                    const firstRow = rows[0];
                                    const symbol = firstRow.Symbol;
                                    const strike = firstRow.Strike;
                                    const type = firstRow.Type;
                                    const exp = firstRow.Exp;
                                    const currentStatus = firstRow.position_status || "Open";

                                    // Correct P&L Logic: rely on Side and use absolute Qty
                                    let netPnL = 0;
                                    rows.forEach(r => {
                                        const qty = Math.abs(r.Qty || 0);
                                        const price = r.Price || 0;
                                        const multiplier = r.contract_multiplier || 100;
                                        const amount = qty * price * multiplier;
                                        const side = (r.Side || "").toUpperCase();
                                        if (side === "BUY") netPnL -= amount;
                                        else if (side === "SELL") netPnL += amount;
                                    });

                                    return (
                                        <div 
                                            key={groupObj.key} 
                                            style={{ 
                                                border: "1px solid #333", // Changed from #ddd
                                                borderRadius: 8, 
                                                padding: 16,
                                                background: "#111", // Changed from #fff
                                                boxShadow: "0 1px 3px rgba(0,0,0,0.5)",
                                                color: "#fff" // Global text contrast for card
                                            }}
                                        >
                                            {/* Header */}
                                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                                                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                                                    <span style={{
                                                        background: getBadgeColor(currentStatus), 
                                                        color: "#fff", 
                                                        padding: "4px 8px", 
                                                        borderRadius: 4, 
                                                        fontSize: 11, 
                                                        fontWeight: "bold",
                                                        textTransform: "uppercase"
                                                    }}>
                                                        {currentStatus}
                                                    </span>
                                                    <span style={{ fontWeight: "bold", fontSize: 16, color: "#fff" }}>{symbol}</span>
                                                    <span style={{ fontSize: 15, color: "#fff" }}>{strike} {type}</span>
                                                    <span style={{ color: "#aaa", fontSize: 14 }}>Exp: {exp}</span>
                                                </div>
                                                <div style={{ textAlign: "right" }}>
                                                    <div style={{ fontSize: 12, color: "#aaa", fontWeight: 600 }}>OUTCOME</div>
                                                    <div style={{ 
                                                        fontWeight: "bold", 
                                                        color: netPnL >= 0 ? "#4CAF50" : "#F44336",
                                                        fontSize: 17 
                                                    }}>
                                                        {netPnL >= 0 ? "Credit" : "Debit"} ${Math.abs(netPnL).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Timeline / Legs */}
                                            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                                                {rows.map((row, idx) => {
                                                    const isSideBuy = (row.Side || "").toUpperCase() === "BUY";
                                                    const isSideSell = (row.Side || "").toUpperCase() === "SELL";
                                                    const sideColor = isSideBuy ? "#2196F3" : (isSideSell ? "#F44336" : "#333");

                                                    return (
                                                        <div 
                                                            key={idx}
                                                            style={{ 
                                                                display: "flex", 
                                                                alignItems: "center", 
                                                                gap: 16, 
                                                                padding: "6px 0",
                                                                borderTop: "1px solid #333" // Changed from #f0f0f0
                                                            }}
                                                            className="lifecycle-row"
                                                        >
                                                            <div style={{ width: 85, fontSize: 13, color: "#aaa" }}>
                                                                {row.Trade_Date}
                                                            </div>
                                                            <div style={{ width: 60, fontWeight: "bold", color: sideColor, fontSize: 13 }}>
                                                                {row.Side}
                                                            </div>
                                                            <div style={{ width: 110, fontSize: 13, fontWeight: 500, color: "#fff" }}>
                                                                {row.Pos_Effect}
                                                            </div>
                                                    
                                                            <div style={{ flex: 1, display: "flex", gap: 24, fontSize: 13, color: "#eee" }} className="secondary-cols">
                                                                <span>Qty: <b>{Math.abs(row.Qty)}</b></span>
                                                                <span>Price: <b>${row.Price?.toFixed(2)}</b></span>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>

                                            {/* Footer: Status Selector */}
                                            <div style={{ marginTop: 12, paddingTop: 12, borderTop: "2px solid #333", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                                <div style={{ fontSize: 13, color: "#aaa" }}>
                                                    {currentStatus === "Assigned" && "⚠️ Resulted in Assignment"}
                                                    {currentStatus === "Expired" && "⏳ Contract Expired Worthless"}
                                                </div>
                                                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                                    <span style={{ fontSize: 13, color: "#ccc" }}>Status:</span>
                                                    <select
                                                        value={currentStatus}
                                                        disabled={!!statusUpdating[groupObj.key]}
                                                        onChange={async e => {
                                                            const newStatus = e.target.value;
                                                            await handleStatusChange(firstRow, newStatus, groupObj.key);
                                                        }}
                                                        style={{ padding: "3px 8px", borderRadius: 4, border: "1px solid #444", fontSize: 13, background: "#222", color: "#fff" }}
                                                    >
                                                        {POSITION_STATUS_OPTIONS.filter(o => o !== "All").map(opt => (
                                                            <option key={opt} value={opt}>{opt}</option>
                                                        ))}
                                                    </select>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                );
            })}
            <style>{`
                .lifecycle-row .secondary-cols {
                    opacity: 0.7;
                    transition: opacity 0.2s;
                }
                .lifecycle-row:hover .secondary-cols {
                    opacity: 1 !important;
                }
            `}</style>
        </div>
    );
}

        export default OptionsTradeTable;